import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, getDocs, deleteDoc, updateDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { BUSINESS_ENTITIES } from '../firebase/firestore';

function PartTimerManualEntry({ selectedMonth, selectedYear, businessEntity, onChangePeriod }) {
  const [manualEntries, setManualEntries] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const getDefaultFormData = useCallback(() => ({
    name: '',
    department: '',
    businessEntity: businessEntity || BUSINESS_ENTITIES[0].id,
    workHours: '',
    hourlyWage: '',
    basicPay: '',
    weeklyAllowance: '',
    totalPay: '',
    businessTax: '',
    netPay: '',
    notes: ''
  }), [businessEntity]);
  const [formData, setFormData] = useState(getDefaultFormData());
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(false);

  const updatePeriod = ({ year, month }) => {
    if (onChangePeriod) {
      onChangePeriod({ year, month });
    }
  };

  const handleYearSelect = (event) => {
    updatePeriod({ year: parseInt(event.target.value, 10), month: selectedMonth });
  };

  const handleMonthSelect = (event) => {
    updatePeriod({ year: selectedYear, month: parseInt(event.target.value, 10) });
  };

  const shiftMonth = (offset) => {
    const base = new Date(selectedYear, selectedMonth + offset, 1);
    updatePeriod({ year: base.getFullYear(), month: base.getMonth() });
  };

  const canChangePeriod = Boolean(onChangePeriod);
  const yearOptions = Array.from({ length: 7 }, (_, idx) => selectedYear - 3 + idx);
  const monthOptions = Array.from({ length: 12 }, (_, idx) => idx);

  const renderPeriodToolbar = () => (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          disabled={!canChangePeriod}
          className={`px-3 py-2 rounded border border-gray-300 text-sm ${canChangePeriod ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
        >
          이전달
        </button>
        <select
          value={selectedYear}
          onChange={handleYearSelect}
          disabled={!canChangePeriod}
          className="px-3 py-2 border rounded"
        >
          {yearOptions.map((year) => (
            <option key={year} value={year}>
              {year}년
            </option>
          ))}
        </select>
        <select
          value={selectedMonth}
          onChange={handleMonthSelect}
          disabled={!canChangePeriod}
          className="px-3 py-2 border rounded"
        >
          {monthOptions.map((month) => (
            <option key={month} value={month}>
              {month + 1}월
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          disabled={!canChangePeriod}
          className={`px-3 py-2 rounded border border-gray-300 text-sm ${canChangePeriod ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
        >
          다음달
        </button>
      </div>
      <div className="text-sm text-gray-500">
        조회 중: {selectedYear}년 {selectedMonth + 1}월
      </div>
    </div>
  );

  const loadManualEntries = useCallback(async () => {
    if (!businessEntity) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, 'partTimerManualEntries'),
        where('year', '==', selectedYear),
        where('month', '==', selectedMonth + 1),
        where('businessEntity', '==', businessEntity)
      );
      const snapshot = await getDocs(q);
      const entries = [];
      snapshot.forEach((doc) => {
        entries.push({ id: doc.id, ...doc.data() });
      });
      setManualEntries(entries);
    } catch (error) {
      console.error('수기 입력 데이터 로드 오류:', error);
    }
    setLoading(false);
  }, [businessEntity, selectedMonth, selectedYear]);

  useEffect(() => {
    if (businessEntity) {
      loadManualEntries();
    }
  }, [businessEntity, loadManualEntries]);

  useEffect(() => {
    if (!editingEntry) {
      setFormData(prev => ({
        ...prev,
        businessEntity: businessEntity || BUSINESS_ENTITIES[0].id
      }));
    }
  }, [businessEntity, editingEntry]);

  const calculateTotals = () => {
    const workHours = Number(formData.workHours) || 0;
    const hourlyWage = Number(formData.hourlyWage) || 0;
    const basicPay = workHours * hourlyWage;
    const weeklyAllowance = Number(formData.weeklyAllowance) || 0;
    const totalPay = basicPay + weeklyAllowance;
    const businessTax = Math.floor(totalPay * 0.033);
    const netPay = totalPay - businessTax;

    setFormData({
      ...formData,
      basicPay: basicPay,
      totalPay: totalPay,
      businessTax: businessTax,
      netPay: netPay
    });
  };

  const resetForm = () => {
    setFormData(getDefaultFormData());
    setEditingEntry(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const timestamp = new Date().toISOString();
      const payload = {
        ...formData,
        year: selectedYear,
        month: selectedMonth + 1,
        businessEntity: formData.businessEntity,
        isManual: true,
        updatedAt: timestamp
      };

      if (editingEntry) {
        await updateDoc(doc(db, 'partTimerManualEntries', editingEntry.id), payload);
        alert('수기 입력이 수정되었습니다.');
      } else {
        await addDoc(collection(db, 'partTimerManualEntries'), {
          ...payload,
          createdAt: timestamp
        });
        alert('수기 입력이 저장되었습니다.');
      }

      resetForm();
      setShowAddForm(false);
      loadManualEntries();
    } catch (error) {
      console.error('저장 오류:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (entryId) => {
    if (window.confirm('정말로 삭제하시겠습니까?')) {
      try {
        await deleteDoc(doc(db, 'partTimerManualEntries', entryId));
        alert('삭제되었습니다.');
        if (editingEntry?.id === entryId) {
          resetForm();
          setShowAddForm(false);
        }
        loadManualEntries();
      } catch (error) {
        console.error('삭제 오류:', error);
        alert('삭제 중 오류가 발생했습니다.');
      }
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      name: entry.name || '',
      department: entry.department || '',
      businessEntity: entry.businessEntity || businessEntity || BUSINESS_ENTITIES[0].id,
      workHours: entry.workHours ?? '',
      hourlyWage: entry.hourlyWage ?? '',
      basicPay: entry.basicPay ?? '',
      weeklyAllowance: entry.weeklyAllowance ?? '',
      totalPay: entry.totalPay ?? '',
      businessTax: entry.businessTax ?? '',
      netPay: entry.netPay ?? '',
      notes: entry.notes || ''
    });
    setShowAddForm(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  return (
    <div className="container mx-auto p-4">
      {renderPeriodToolbar()}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold">
              파트타이머 수기 입력 - {selectedYear}년 {selectedMonth + 1}월
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              사업자: {BUSINESS_ENTITIES.find(e => e.id === businessEntity)?.name || '선택 안됨'}
            </p>
          </div>
          <button
            onClick={() => {
              if (showAddForm) {
                resetForm();
                setShowAddForm(false);
              } else {
                resetForm();
                setShowAddForm(true);
              }
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {showAddForm ? '취소' : '수기 입력 추가'}
          </button>
        </div>

        {loading && (
          <div className="mb-4 text-sm text-blue-600">
            데이터를 불러오는 중입니다...
          </div>
        )}

        {showAddForm && (
          <div className="mb-6 p-4 border rounded bg-gray-50">
            <h3 className="text-lg font-bold mb-4">{editingEntry ? '수기 급여 수정' : '수기 급여 입력'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">이름 *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">사업자 *</label>
                  <select
                    required
                    value={formData.businessEntity}
                    onChange={(e) => setFormData({...formData, businessEntity: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                  >
                    {BUSINESS_ENTITIES.map(entity => (
                      <option key={entity.id} value={entity.id}>
                        {entity.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">부서</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">근무시간</label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.workHours}
                    onChange={(e) => setFormData({...formData, workHours: e.target.value})}
                    onBlur={calculateTotals}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">시급</label>
                  <input
                    type="number"
                    value={formData.hourlyWage}
                    onChange={(e) => setFormData({...formData, hourlyWage: e.target.value})}
                    onBlur={calculateTotals}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">기본급</label>
                  <input
                    type="number"
                    value={formData.basicPay}
                    onChange={(e) => setFormData({...formData, basicPay: e.target.value})}
                    className="w-full px-3 py-2 border rounded bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">주휴수당</label>
                  <input
                    type="number"
                    value={formData.weeklyAllowance}
                    onChange={(e) => setFormData({...formData, weeklyAllowance: e.target.value})}
                    onBlur={calculateTotals}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">총급여</label>
                  <input
                    type="number"
                    value={formData.totalPay}
                    onChange={(e) => setFormData({...formData, totalPay: e.target.value})}
                    className="w-full px-3 py-2 border rounded bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">사업소득세(3.3%)</label>
                  <input
                    type="number"
                    value={formData.businessTax}
                    onChange={(e) => setFormData({...formData, businessTax: e.target.value})}
                    className="w-full px-3 py-2 border rounded bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">실수령액</label>
                  <input
                    type="number"
                    value={formData.netPay}
                    onChange={(e) => setFormData({...formData, netPay: e.target.value})}
                    className="w-full px-3 py-2 border rounded bg-gray-100 font-bold"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">비고</label>
                  <input
                    type="text"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="추가 메모 사항"
                  />
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded mb-4">
                <p className="text-sm">
                  💡 근무시간과 시급을 입력하면 자동으로 계산됩니다.
                  필요시 각 금액을 직접 수정할 수 있습니다.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowAddForm(false);
                  }}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  {editingEntry ? '수정 완료' : '저장'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2">이름</th>
                <th className="border px-4 py-2">사업자</th>
                <th className="border px-4 py-2">부서</th>
                <th className="border px-4 py-2">근무시간</th>
                <th className="border px-4 py-2">시급</th>
                <th className="border px-4 py-2">기본급</th>
                <th className="border px-4 py-2">주휴수당</th>
                <th className="border px-4 py-2">총급여</th>
                <th className="border px-4 py-2">사업소득세</th>
                <th className="border px-4 py-2">실수령액</th>
                <th className="border px-4 py-2">비고</th>
                <th className="border px-4 py-2">작업</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="border px-4 py-8 text-center">
                    로딩 중...
                  </td>
                </tr>
              ) : manualEntries.length > 0 ? (
                manualEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="border px-4 py-2 font-bold">{entry.name}</td>
                    <td className="border px-4 py-2">
                      {BUSINESS_ENTITIES.find(e => e.id === entry.businessEntity)?.name || entry.businessEntity || '-'}
                    </td>
                    <td className="border px-4 py-2">{entry.department || '-'}</td>
                    <td className="border px-4 py-2 text-center">{entry.workHours || '-'}</td>
                    <td className="border px-4 py-2 text-right">
                      {entry.hourlyWage ? `₩${formatCurrency(entry.hourlyWage)}` : '-'}
                    </td>
                    <td className="border px-4 py-2 text-right">
                      ₩{formatCurrency(entry.basicPay || 0)}
                    </td>
                    <td className="border px-4 py-2 text-right">
                      ₩{formatCurrency(entry.weeklyAllowance || 0)}
                    </td>
                    <td className="border px-4 py-2 text-right font-bold">
                      ₩{formatCurrency(entry.totalPay || 0)}
                    </td>
                    <td className="border px-4 py-2 text-right text-red-600">
                      ₩{formatCurrency(entry.businessTax || 0)}
                    </td>
                    <td className="border px-4 py-2 text-right font-bold text-blue-600">
                      ₩{formatCurrency(entry.netPay || 0)}
                    </td>
                    <td className="border px-4 py-2 text-sm">{entry.notes || '-'}</td>
                    <td className="border px-4 py-2">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleEdit(entry)}
                          className="bg-green-500 text-white px-2 py-1 rounded text-sm hover:bg-green-600"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="bg-red-500 text-white px-2 py-1 rounded text-sm hover:bg-red-600"
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="border px-4 py-8 text-center text-gray-500">
                    수기 입력된 데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
            {manualEntries.length > 0 && (
              <tfoot>
                <tr className="bg-gray-200 font-bold">
                  <td colSpan="5" className="border px-4 py-2 text-right">합계:</td>
                  <td className="border px-4 py-2 text-right">
                    ₩{formatCurrency(
                      manualEntries.reduce((sum, entry) => sum + (entry.basicPay || 0), 0)
                    )}
                  </td>
                  <td className="border px-4 py-2 text-right">
                    ₩{formatCurrency(
                      manualEntries.reduce((sum, entry) => sum + (entry.weeklyAllowance || 0), 0)
                    )}
                  </td>
                  <td className="border px-4 py-2 text-right">
                    ₩{formatCurrency(
                      manualEntries.reduce((sum, entry) => sum + (entry.totalPay || 0), 0)
                    )}
                  </td>
                  <td className="border px-4 py-2 text-right text-red-600">
                    ₩{formatCurrency(
                      manualEntries.reduce((sum, entry) => sum + (entry.businessTax || 0), 0)
                    )}
                  </td>
                  <td className="border px-4 py-2 text-right text-blue-600">
                    ₩{formatCurrency(
                      manualEntries.reduce((sum, entry) => sum + (entry.netPay || 0), 0)
                    )}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

export default PartTimerManualEntry;
